import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = ({ onOpenContactForm }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 150);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { label: 'Services', id: 'services' },
    { label: 'Process', id: 'process' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Testimonials', id: 'testimonials' },
    { label: 'FAQ', id: 'faq' },
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm'
          : 'bg-white/60 backdrop-blur-lg'
      }`}
    >
      {/* Top strip */}
      <div className="bg-[#0A0F1C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between py-1.5 text-xs tracking-wide">
          <a href="mailto:info@trademarklogodesign.com" className="text-gray-400 hover:text-white transition-colors hidden sm:block">
            info@trademarklogodesign.com
          </a>
          <a href="tel:+18005551234" className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors">
            <Phone className="w-3 h-3" />
            (800) 555-1234
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-4">
        <div className="flex items-center justify-between">
          <button
            data-testid="header-logo"
            onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="cursor-pointer"
          >
            <span className="text-xl font-bold tracking-tight text-[#0A0F1C]">
              Trademark <span className="text-[#002FA7]">Logo Design</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => (
              <button
                key={link.id}
                data-testid={`nav-${link.id}`}
                onClick={() => scrollToSection(link.id)}
                className="hover-underline px-4 py-2 text-[13px] font-medium text-[#4B5563] hover:text-[#002FA7] transition-colors"
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Button
              data-testid="header-cta-button"
              onClick={onOpenContactForm}
              className="bg-[#002FA7] hover:bg-[#002280] text-white font-medium px-6 h-10 text-[13px] rounded-none transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,47,167,0.25)]"
            >
              Free Consultation
            </Button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-[#0A0F1C] p-1"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div data-testid="mobile-menu" className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
            <nav className="flex flex-col gap-0">
              {navLinks.map(link => (
                <button
                  key={link.id}
                  data-testid={`mobile-nav-${link.id}`}
                  onClick={() => scrollToSection(link.id)}
                  className="text-left px-3 py-3 text-sm font-medium text-[#4B5563] hover:text-[#002FA7] border-b border-gray-100 transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-4 mt-2">
                <Button
                  data-testid="mobile-cta-button"
                  onClick={onOpenContactForm}
                  className="w-full bg-[#002FA7] hover:bg-[#002280] text-white font-medium h-11 rounded-none text-sm"
                >
                  Free Consultation
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
